import Vue from 'vue'
import Router from 'vue-router'
import AdminRoutesGenerator from '@/api/AdminRoutesGenerator'
import {
  requireAuth,
  requireAdmin,
  requireManager,
  requireHotelAccount,
  requireAdminContingents,
  isAdminContingents, redirectToHome
} from '@/utils/auth'
import { getDomain } from '@/utils/domain'

Vue.use(Router)

export const accessibleRoutesWithoutAuthentication = [
  'login',
  'public_booking_show',
  'public_booking_edit',
  'booking_validate',
  'offline_booking_prelogged_group',
  'offline_booking_prelogged_group_company',
  'home_generic_company',
  'offline_booking_generic_company',
  'create_account',
  'define_password',
  'public_survey_question'
]

let backRoute = './views/backoffice/'
let frontRoute = './views/frontoffice/'
let formationRoute = './views/formation/'
let contingentsRoute = './views/contingents/'

let adminGenerator = new AdminRoutesGenerator()
let router
let domain = getDomain()

switch (domain) {
  case 'contingents':
    router = new Router({
      routes: [
        {
          path: '/login',
          name: 'loginContingents',
          component: () => import(contingentsRoute + 'login.vue')
        },
        {
          path: '/redirect',
          name: 'redirect',
          beforeEnter: (to, from, next)=> {
            redirectToHome(router)
          }
        },
        {
          path: '/',
          name: 'contingents',
          component: () => import(contingentsRoute + 'home.vue'),
          beforeEnter: requireHotelAccount,
          children: [
            {
              path: '/',
              name: 'hotel_details',
              component: () => import(contingentsRoute + 'hotel_details.vue')
            }
          ]
            .concat(adminGenerator.generateRoutes('hotel'))
        },
        {
          path: '/define_password/:uid',
          name: 'define_password',
          component: () => import(contingentsRoute + 'definePassword.vue')
        },
        {
          path: '/admin',
          name: 'admin_contingents',
          component: () => import(contingentsRoute + 'admin/home.vue'),
          beforeEnter: requireAdminContingents,
          children: [
            {
              path: 'hotels',
              name: 'contingents_hotel_list',
              component: () => import(contingentsRoute + 'admin/hotels.vue')
            },
            {
              path: 'contingents',
              name: 'contingents_list',
              component: () => import(contingentsRoute + 'admin/contingents.vue')
            },
            {
              path: 'retrocessions',
              name: 'contingents_retrocession_list',
              component: () => import(contingentsRoute + 'admin/retrocessions.vue')
            },
            {
              path: 'bookings',
              name: 'contingents_bookings_list',
              component: () => import(contingentsRoute + 'admin/bookings.vue')
            }
          ]
            .concat(adminGenerator.generateRoutes('contingent'))
        }
      ]
    })
    break
  case 'formation':
    router = new Router({
      routes: [
        {
          path: '/',
          name: 'formation',
          component: () => import(formationRoute + 'formation.vue')
        },
        {
          path: '/offline',
          name: 'formation_offline',
          component: () => import(formationRoute + 'offline.vue')
        },
        {
          path: '/online',
          name: 'online',
          component: () => import(formationRoute + 'online.vue'),
          redirect: {
            name: 'home_search'
          },
          children: [
            {
              path: 'home_search',
              name: 'home_search',
              component: () => import(frontRoute + 'homeSearch.vue')
            },
            {
              path: 'online/results',
              name: 'online_results',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHotelAvailabilities.vue')
            },
            {
              path: 'online/discover/:hotelKey',
              name: 'online_discover',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHotelDiscover.vue')
            },
            {
              path: 'online/offers/:hotelKey',
              name: 'online_offers',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHotelAvailDetail.vue')
            },
            {
              path: 'online/booking/completion/:action?',
              name: 'online_booking_completion',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingCompletion.vue')
            },
            {
              path: 'booking/summary',
              name: 'booking_summary',
              component: () => import(frontRoute + 'bookings/summary.vue')
            },
            {
              path: 'booking/:id/confirm/:online?/:requireValidation?/:requireOrderNumber?',
              name: 'booking_request_confirm',
              component: () => import(frontRoute + 'bookings/requestConfirm.vue')
            },
            {
              path: 'booking/:id/show',
              name: 'home_booking_show',
              component: () => import(frontRoute + 'bookings/show.vue')
            },
            {
              path: 'message/create/:id?',
              name: 'home_message_create',
              component: () => import(frontRoute + 'message/create.vue')
            }
          ]
        }
      ]
    })
    break
  default:
    router = new Router({
      routes: [
        {
          path: '/login',
          name: 'login',
          component: () => import('./views/login.vue')
        },
        {
          path: '/public/booking/:token/show',
          name: 'public_booking_show',
          component: () => import(frontRoute + 'bookings/public/show.vue')
        },
        {
          path: '/public/booking/:token/edit',
          name: 'public_booking_edit',
          component: () => import(frontRoute + 'bookings/public/edit.vue')
        },
        {
          path: '/booking/:uid/validate',
          name: 'booking_validate',
          component: () => import(frontRoute + 'bookings/validate.vue')
        },
        {
          path: '/create_account/:companyUid?',
          name: 'create_account',
          component: () => import(frontRoute + 'createAccount.vue')
        },
        {
          path: '/define_password/:uid',
          name: 'define_password',
          component: () => import(frontRoute + 'definePassword.vue')
        },
        {
          path: '/public/survey/:token/questions',
          name: 'public_survey_question',
          component: () => import(frontRoute + 'public/satisfactionSurvey')
        },
        {
          path: '/offline/booking/prelogged/group/:groupAlias',
          name: 'offline_booking_prelogged_group',
          component: () => import(frontRoute + 'bookings/offlineBooking/groupPrelogged.vue')
        },
        {
          path: '/offline/booking/prelogged/company/:companyUid/:values',
          name: 'offline_booking_prelogged_company',
          component: () => import(frontRoute + 'bookings/offlineBooking/preLoggedAccount.vue')
        },
        {
          path: '/gen/:companyUid',
          name: 'home_generic_company',
          component: () => import(frontRoute + 'homeGeneric.vue')
        },
        {
          path: '/offline/booking/gen/:companyUid',
          name: 'offline_booking_generic_company',
          component: () => import(frontRoute + 'bookings/offlineBooking/offlineBookingHome.vue')
        },
        {
          path: '/admin',
          name: 'admin',
          component: () => import(backRoute + 'home.vue'),
          beforeEnter: requireAdmin,
          // redirect: '/admin/company/list',
          children: [
            {
              path: '/admin/cb',
              name: 'admin_cb',
              component: () => import(backRoute + 'admin/cb/show.vue'),
              beforeEnter: requireAdmin
            },
            {
              path: '/admin/cb/edit',
              name: 'admin_cb_edit',
              component: () => import(backRoute + 'admin/cb/edit.vue'),
              beforeEnter: requireAdmin
            }
          ]
            .concat(adminGenerator.generateRoutes('backoffice'))
        },
        {
          path: '/',
          name: 'home',
          component: () => import(frontRoute + 'home.vue'),
          redirect: {
            name: 'home_search'
          },
          beforeEnter: requireAuth,
          children: [
            {
              path: 'help',
              name: 'home_help_show',
              component: () => import(frontRoute + 'help.vue')
            },
            {
              path: 'home_search',
              name: 'home_search',
              component: () => import(frontRoute + 'homeSearch.vue')
            },
            {
              path: 'online/home',
              name: 'online_home',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHome.vue')
            },
            {
              path: 'online/results',
              name: 'online_results',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHotelAvailabilities.vue')
            },
            {
              path: 'online/offers/:hotelKey',
              name: 'online_offers',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHotelAvailDetail.vue')
            },
            {
              path: 'online/discover/:hotelKey',
              name: 'online_discover',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingHotelDiscover.vue')
            },
            {
              path: 'online/booking/completion/:action?',
              name: 'online_booking_completion',
              component: () => import(frontRoute + 'bookings/onlineBooking/onlineBookingCompletion.vue')
            },
            {
              path: 'offline/home',
              name: 'offline_home',
              component: () => import(frontRoute + 'bookings/offlineBooking/offlineBookingHome.vue')
            },
            {
              path: 'offline/:id/edit',
              name: 'offline_booking_edit',
              component: () => import(frontRoute + 'bookings/offlineBooking/offlineBookingHome.vue')
            },
            {
              path: 'booking/:id/show',
              name: 'home_booking_show',
              component: () => import(frontRoute + 'bookings/show.vue')
            },
            {
              path: 'booking/:id/edit',
              name: 'home_booking_edit',
              component: () => import(frontRoute + 'bookings/edit.vue')
            },
            {
              path: 'booking/:id/confirm/:online?/:requireValidation?/:requireOrderNumber?',
              name: 'booking_request_confirm',
              component: () => import(frontRoute + 'bookings/requestConfirm.vue')
            },
            {
              path: 'request/confirm',
              name: 'offline_request_confirm',
              component: () => import(frontRoute + 'bookings/requestConfirm.vue')
            },
            {
              path: 'booking/summary',
              name: 'booking_summary',
              component: () => import(frontRoute + 'bookings/summary.vue')
            },
            {
              path: 'booking/:id/duplicate',
              name: 'home_booking_duplicate',
              component: () => import(frontRoute + 'bookings/duplicate.vue')
            },
            {
              path: 'notification/list',
              name: 'home_notification_list',
              component: () => import(frontRoute + 'notification.vue')
            },
            {
              path: 'notification/:id/show',
              name: 'home_notification_show'
            },
            {
              path: 'message/list',
              name: 'home_message_list',
              component: () => import(frontRoute + 'message/list.vue')
            },
            {
              path: 'message/create/:id?',
              name: 'home_message_create',
              component: () => import(frontRoute + 'message/create.vue')
            },
            {
              path: 'seminar',
              name: 'seminar',
              component: () => import(frontRoute + 'seminar.vue')
            },
            {
              path: 'group',
              name: 'group',
              component: () => import(frontRoute + 'group.vue')
            },
            {
              path: 'events',
              name: 'events',
              component: () => import(frontRoute + 'events.vue')
            },
            {
              path: 'bookings',
              name: 'bookings',
              component: () => import(frontRoute + 'bookings/bookings.vue')
            },
            {
              path: 'travellers',
              name: 'travellers',
              component: () => import(frontRoute + 'travellers.vue')
            },
            {
              path: 'news',
              name: 'news',
              component: () => import(frontRoute + 'news.vue')
            },
            {
              path: 'survey',
              name: 'home_survey',
              component: () => import(frontRoute + 'survey.vue')
            },
            {
              path: 'profile',
              name: 'home_profile_show',
              component: () => import(frontRoute + 'profile.vue')
            },
            {
              path: 'users',
              name: 'home_users',
              component: () => import(frontRoute + 'users.vue'),
              beforeEnter: requireManager
            }
          ]
        }
      ]
    })
}

router.afterEach(() => {
  router.app.$store.commit('mobileMenu/hide')
})

export default router
